import React from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Eye, AlertTriangle, Clock, Triangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCurrency } from "@shared/selectors/globalSelectors";

import { BUTTON_FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { useChoices } from "@features/cx/paymentsv2/hooks";
import { Button } from "@shared/ui/button";

interface SlipLotsListProps {
  tableData: any[];
  totalCount: number;
  isLoading: boolean;
}

// --- Helper Functions ---
const getStatusBadge = (status: string) => {
  let color = "bg-gray-100 text-gray-800";
  let dot = "bg-gray-500";

  if (status === "Under Entry") {
    color = "bg-blue-50 text-blue-700 border-blue-200";
    dot = "bg-blue-500";
  } else if (status === "Partially Posted") {
    color = "bg-orange-50 text-orange-700 border-orange-200";
    dot = "bg-orange-500";
  } else if (status === "Fully Posted" || status === "Reconciled") {
    color = "bg-green-50 text-green-700 border-green-200";
    dot = "bg-green-500";
  } else if (status === "Closed") {
    color = "bg-purple-50 text-purple-700 border-purple-200";
    dot = "bg-purple-500";
  }

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 w-max ${color}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
      {status}
    </span>
  );
};

import { FilterX } from "lucide-react";

// --- Filter Component ---
const SlipLotsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: slipLotStatusData } = useChoices("slip_lot_status");

  const form = useForm({
    defaultValues: {
      centre: searchParams.get("centre") || "",
      status: searchParams.get("status") || "",
      dateRange: searchParams.get("dateRange") || "",
    },
  });

  const onSubmit = (value: any) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };

    if (value.centre) newParams.centre = value.centre;
    else delete newParams.centre;

    if (value.status) newParams.status = value.status;
    else delete newParams.status;

    if (value.dateRange) newParams.dateRange = value.dateRange;
    else delete newParams.dateRange;

    newParams.page = "1";
    setSearchParams(newParams, { replace: true });
  };

  const handleClear = () => {
    form.reset({
      centre: "",
      status: "",
      dateRange: "",
    });
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    delete newParams.centre;
    delete newParams.status;
    delete newParams.dateRange;
    newParams.page = "1";
    setSearchParams(newParams, { replace: true });
  };

  const slipLotOptions = slipLotStatusData?.map((item: any) => ({
    label: item.value,
    value: item.key,
  }));

  const filterFields: FormField[] = [
    {
      name: "centre",
      label: "Centre",
      type: "select",
      options: [
        { label: "DTW - Downtown Payment Centre", value: "DTW" },
        { label: "WST - Westside Outlet", value: "WST" },
        { label: "APT - Airport Terminal Kiosk", value: "APT" },
        { label: "CTH - City Hall Counter", value: "CTH" },
        { label: "MOL - Mall Outlet — Park Ave", value: "MOL" },
        { label: "GRC - Grocery Chain Partner", value: "GRC" },
      ],
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: slipLotOptions || [],
    },
    {
      name: "dateRange",
      label: "Date Range",
      type: "datepicker",
    },
  ];

  return (
    <div className="w-full flex flex-col gap-4 p-2">
      <DynamicForm
        form={form}
        fields={filterFields}
        gridColumns={3}
        onSubmit={onSubmit}
        onReset={handleClear}
        config={{
          showSubmitButton: true,
          submitButtonText: "Apply Filters",
          showResetButton: true,
          resetButtonText: "Clear",
          resetIcon: FilterX,
          classes: BUTTON_FIELD_CLASSES,
        }}
      />
    </div>
  );
};

// --- Main Component ---
export const SlipLotsList = ({
  tableData,
  totalCount,
  isLoading,
}: SlipLotsListProps) => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || 1);
  const itemsPerPage = PAGE_SIZE;

  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "lotId",
      header: "Lot ID",
      type: "custom",
      render: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{value}</span>
          {row.showSlaBadge && (
            <span className="flex items-center gap-1 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200 font-medium">
              <Clock className="w-3 h-3" /> SLA
            </span>
          )}
        </div>
      ),
    },
    {
      key: "centreName",
      header: "Payment Centre",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="text-sm font-medium">{row.centreCodeDisplay}</div>
          <div className="text-xs text-muted-foreground">{row.centreCode}</div>
        </div>
      ),
    },
    {
      key: "collectionDate",
      header: "Collection Date",
      type: "text",
    },
    {
      key: "physicalSlipCount",
      header: "Physical / Entered",
      type: "custom",
      render: (value: any, row: any) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">
            {value} / <span className="font-semibold">{row.enteredCount}</span>
          </span>
          {row.countDelta ? (
            <span className="flex items-center gap-0.5 text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded border border-yellow-200 font-medium">
              <Triangle className="w-2.5 h-2.5 mb-0.5" /> {row.countDelta}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "validCount",
      header: "Valid / Flagged",
      type: "custom",
      render: (_: any, row: any) => (
        <div className="text-sm font-semibold">
          <span className="text-green-600">{row.validCount}</span> /{" "}
          <span
            className={row.flaggedCount > 0 ? "text-red-500" : "text-slate-400"}
          >
            {row.flaggedCount}
          </span>
        </div>
      ),
    },
    {
      key: "lotTotal",
      header: `Lot Total (${currency})`,
      type: "custom",
      render: (value: number) => (
        <span className="font-semibold text-sm">{value}</span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: string) => getStatusBadge(value),
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdBy ?? "NA"}
          </p>
        </div>
      ),
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: <Eye className="w-4 h-4" />,
      onClick: (row: any) => navigate(`/cx/payments/slip-lots/view/${row.id}`),
    },
  ];

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm">
        <GenericTable
          data={tableData}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search lot ID, centre, date..."
          onSearch={handleSearch}
          filterPanel={<SlipLotsFilter />}
          actions={actions}
          actionsType="icons"
          pagination={{
            currentPage: currentPage,
            totalPages: Math.ceil((totalCount || 0) / itemsPerPage),
            totalItems: totalCount || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, totalCount || 0),
          }}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default SlipLotsList;
